// import { useInView } from "motion/react";
import { getHighlightMarkup, parseBreaksToParagraphs } from "@/core/utils";
import { useInducteeLanding } from "@/providers/InducteeLandingContext";
import { Container } from "@components";
import className from "classnames/bind";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { useViewSize } from "@/providers/ViewSizeContext";
// import { useHeader } from "@/providers/HeaderContext";
import { useClientMount } from "@/providers/ClientMountProvider";

import styles from "./InducteeLandingHeader.module.scss";

import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Tabs from "./Tabs";
const cx = className.bind(styles);

interface InducteeLandingHeaderProps {
  headline: string;
  aZLabel: string;
  byClassLabel: string;
  activeTab: "a-z" | "by-class";
  onTabClick?: (tab: "a-z" | "by-class") => void;
}

const InducteeLandingHeader = forwardRef<
  HTMLDivElement,
  InducteeLandingHeaderProps
>(({ headline, aZLabel, byClassLabel, activeTab, onTabClick }, ref) => {
  const headlineMarkup = parseBreaksToParagraphs(getHighlightMarkup(headline));
  const headlineEl = useRef<HTMLHeadingElement>(null);
  const contentEl = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const tabRootEl = useRef<HTMLDivElement>(null);
  const scrollerEl = useRef<HTMLDivElement>(null);
  const fixedTabStarted = useRef<boolean>(false);
  const tabsScrollRef = useRef<HTMLDivElement>(null);
  const tabsFixedRef = useRef<HTMLDivElement>(null);
  /* const { isHeaderFixed } = useHeader(); */ // Unused - was from old motion/react useInView implementation
  /* const isInView = useInView(tabRootEl, {
    margin: `${isHeaderFixed ? "-55px" : "24px"} 0px 0px 0px`,
    amount: "all",
  }); */ // Old implementation - replaced with custom scroll handler for better performance

  const [isInView, setIsInView] = useState(true);
  const rafRef = useRef<number | null>(null);
  const lastInViewState = useRef<boolean>(true);

  useEffect(() => {
    const handleScroll = () => {
      // Cancel any pending animation frame
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }

      // Schedule the check for the next frame for smoother updates
      rafRef.current = requestAnimationFrame(() => {
        if (!tabRootEl.current) return;

        const rect = tabRootEl.current.getBoundingClientRect();

        // Add hysteresis buffer to prevent flickering at threshold
        // Use different thresholds for scrolling up vs down
        const THRESHOLD_DOWN = 158; // When scrolling down, hide at this point
        const THRESHOLD_UP = 165;   // When scrolling up, show at this point (7px buffer)

        let shouldBeInView: boolean;
        if (lastInViewState.current) {
          // Currently in view - use lower threshold to hide
          shouldBeInView = rect.bottom > THRESHOLD_DOWN;
        } else {
          // Currently out of view - use higher threshold to show
          shouldBeInView = rect.bottom > THRESHOLD_UP;
        }

        // Only update state if it actually changed
        if (shouldBeInView !== lastInViewState.current) {
          lastInViewState.current = shouldBeInView;
          setIsInView(shouldBeInView);
        }
      });
    };

    // Use passive listener for better scroll performance
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Initial check
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [activeTab]); // Re-run when activeTab changes to recalculate

  const { viewWidth, isDesktop, isReady } = useViewSize();
  const { mounted } = useClientMount();
  const tabAffixInner = useRef(false);

  const {
    setHeadlineEl,
    setHeaderEl,
    setContentEl,
    headlineEl: contextHeadlineEl,
    // tabAffix,
    setTabAffix,
    extraFixedTab,
    setExtraFixedTab,
  } = useInducteeLanding();

  // Initialize/reset tab controls on mount or when activeTab changes
  useEffect(() => {
    // Ensure tab controls start in correct state
    tabAffixInner.current = false;
    setTabAffix(false);
    setExtraFixedTab(false);
    // DO NOT reset fixedTabStarted here - it should only be set by animation completion
    // fixedTabStarted.current = false;
    setIsInView(true);
  }, []);

  useEffect(() => {
    let tween: gsap.core.Tween = undefined;
    if (activeTab === "by-class" && scrollerEl.current && isDesktop) {
      tween = gsap.to(scrollerEl.current, {
        y: "-500vh",
        scrollTrigger: {
          scrub: 1,
          id: 'scroller-by-class', // Add ID for easier cleanup
        },
        onComplete: () => {},
      });
    }

    return () => {
      // Properly kill the tween and its ScrollTrigger
      if (tween) {
        const st = ScrollTrigger.getById('scroller-by-class');
        if (st) st.kill();
        tween.kill();
        tween = null;
      }

      // Reset scroller position when switching tabs
      if (scrollerEl.current) {
        gsap.set(scrollerEl.current, {
          clearProps: "y",
        });
      }
    };
  }, [activeTab, isDesktop]); // Remove scrollerEl.current from deps to avoid re-running unnecessarily

  useEffect(() => {
    if (viewWidth) {
      ScrollTrigger.refresh(true);
    }
  }, [viewWidth]);

  useEffect(() => {
    setHeadlineEl(headlineEl.current);
    setHeaderEl(rootRef.current);
    setContentEl(contentEl.current);
  }, [headlineEl]);

  // Reset tab controls state when activeTab changes (e.g., browser back button)
  useEffect(() => {
    // Reset tab affix states (but NOT fixedTabStarted - that controls sticky behavior)
    tabAffixInner.current = false;
    setTabAffix(false);
    setExtraFixedTab(false);

    // Reset visibility
    setIsInView(true);

    // DO NOT reset fixedTabStarted - it needs to stay true for sticky behavior
    // fixedTabStarted.current controls whether the tab can stick, not which tab is active

    // Kill any GSAP animations on tab-related elements
    if (tabRootEl.current) {
      gsap.killTweensOf(tabRootEl.current);
    }
    if (tabsScrollRef.current) {
      gsap.killTweensOf(tabsScrollRef.current);
    }
    if (tabsFixedRef.current) {
      gsap.killTweensOf(tabsFixedRef.current);
    }
  }, [activeTab]);

  // Update tab affix state when isInView changes
  // No debouncing needed since we're already using RAF in scroll handler
  useEffect(() => {
    if (fixedTabStarted.current) {
      const shouldAffix = !isInView;
      if (tabAffixInner.current !== shouldAffix) {
        tabAffixInner.current = shouldAffix;
        setTabAffix(shouldAffix);
      }
    }
  }, [isInView, setTabAffix]);

  useGSAP(() => {
    // Don't run animations until client is mounted and viewport is ready
    if (!mounted || !isReady) {
      return;
    }

    gsap.registerPlugin(ScrollTrigger);
    if (!contextHeadlineEl || !contentEl.current) {
      return;
    }

    // Clear any existing inline styles from previous animations
    gsap.set(contextHeadlineEl.children, { clearProps: "all" });
    gsap.set(tabRootEl.current, { clearProps: "all" });

    gsap.set(contextHeadlineEl.querySelectorAll(".highlighted-text"), {
      opacity: 1,
    });

    gsap.fromTo(
      contextHeadlineEl.children,
      {
        y: "130%",
        willChange: "transform",
        clipPath: "rect(0% 100% 0% 0%)",
        skewY: 1,
        opacity: 1,
      },
      {
        y: "0%",
        ease: "power4.out",
        stagger: 0.1,
        willChange: "transform",
        clipPath: "rect(0% 100% 100% 0%)",
        duration: 1.4,
        skewY: 0,
        opacity: 1,
        onComplete: () => {
          Array.from(contextHeadlineEl.children)
            .reverse()
            .forEach((el: Element, index) => {
              gsap.to(el, {
                y: `-${index * 120}%`,
                scrollTrigger: {
                  scrub: 1,
                  id: `headline-scroll-${index}`, // Add ID for cleanup
                },
              });
            });
        },
      }
    );
    gsap.fromTo(
      tabRootEl.current,
      {
        y: 30,
        opacity: 0,
      },
      {
        y: 0,
        opacity: 1,
        duration: 1,
        ease: "power4.out",
        delay: contextHeadlineEl.children.length * 0.1 + 0.3,
        onComplete: () => {
          fixedTabStarted.current = true;
        },
      }
    );

    // Cleanup function
    return () => {
      // Kill all ScrollTriggers created by this component
      ScrollTrigger.getAll().forEach((trigger) => {
        if (trigger.vars.id?.startsWith('headline-scroll-')) {
          trigger.kill();
        }
      });
    };
  }, [contextHeadlineEl, mounted, isReady]);

  const tabClick = (tab: "a-z" | "by-class") => {
    if (tab === activeTab) {
      return;
    }

    // Clear GSAP transforms on scroller element
    gsap.set(scrollerEl.current, {
      clearProps: "y",
    });

    // Kill any active ScrollTriggers on the scroller element
    ScrollTrigger.getAll().forEach((trigger) => {
      if (trigger.vars.trigger === scrollerEl.current) {
        trigger.kill();
      }
    });

    // Invoke the parent callback (this triggers scroll to top)
    onTabClick(tab);

    // Delay resetting tab affix states until after scroll animation completes
    // This prevents the fixed tabs from instantly disappearing when switching tabs while scrolled down
    gsap.delayedCall(0.6, () => {
      // Reset all tab affix states
      setTabAffix(false);
      setExtraFixedTab(false);

      // CRITICAL: Reset the tabAffixInner ref that controls tab visibility
      tabAffixInner.current = false;

      // Reset visibility state
      setIsInView(true);
    });

    // DO NOT reset fixedTabStarted - sticky behavior needs this to stay true
    // fixedTabStarted.current = false; // REMOVED
  };

  return (
    <div className={cx(["inductee-landing-header"])} ref={rootRef}>
      <div ref={scrollerEl}>
        <Container className={cx("container")} ref={ref}>
          <div className="content" ref={contentEl}>
            <h1 className={cx(["headline", "display-xl"])} ref={headlineEl}>
              {headlineMarkup.map((line, index) => (
                <span key={index} dangerouslySetInnerHTML={{ __html: line }} />
              ))}
            </h1>

            <div ref={tabRootEl}>
              <Tabs
                isFixed={false}
                isVisible={!tabAffixInner.current}
                aZLabel={aZLabel}
                byClassLabel={byClassLabel}
                onTabClick={tabClick}
                activeTab={activeTab}
                elRef={tabsScrollRef}
              />
            </div>
          </div>
        </Container>
      </div>

      <Tabs
        isFixed
        isVisible={tabAffixInner.current}
        aZLabel={aZLabel}
        byClassLabel={byClassLabel}
        onTabClick={tabClick}
        activeTab={activeTab}
        elRef={tabsFixedRef}
      />

      <Tabs
        isFixed
        isVisible={extraFixedTab}
        aZLabel={aZLabel}
        byClassLabel={byClassLabel}
        onTabClick={tabClick}
        activeTab={activeTab}
      />
    </div>
  );
});

export default InducteeLandingHeader;
